import React, { useEffect } from "react";
import { Modal, Form, Input, InputNumber, DatePicker, App } from "antd";
import { useUpdate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import type { IncidenceDetail } from "../../../types";
import dayjs from "dayjs";

interface ChangeStatusModalProps {
  visible: boolean;
  onCancel: () => void;
  incidence: IncidenceDetail;
}

interface CloseIncidenceFormValues {
  resolution_notes: string;
  resolved_date: string;
  regularized_surface?: number;
}

/**
 * ChangeStatusModal Component (renamed to CloseIncidenceModal conceptually)
 * Modal for closing an incidence
 * Features:
 * - Textarea for resolution notes (required)
 * - DatePicker for closure date (required, defaults to today)
 * - Input for regularized surface (only for "Superficie irregular" type)
 * - Validation of required fields
 * - Sends to PATCH /api/incidences/{id}/change-status/
 * - Refreshes view after successful closure
 */
export const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  visible,
  onCancel,
  incidence,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const { mutate: updateStatus } = useUpdate();

  // Check if incidence is "Superficie irregular" type
  const isSurfaceIrregular =
    incidence.kind_incidence_detail?.name === "Superficie irregular";

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      // Set default date to today
      form.setFieldsValue({
        resolved_date: dayjs(),
      });
    }
  }, [visible, form]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // After the Ticket unification, closing an incidence is a
      // regular PATCH on the ticket. The backend allows the creator
      // to send the resolution payload (notes, date, surface) as
      // part of the same call when status=2.
      const statusData: any = {
        status: 2, // resolved
        resolution_notes: values.resolution_notes,
        resolved_date: values.resolved_date.format("YYYY-MM-DD"),
      };

      if (
        isSurfaceIrregular &&
        values.regularized_surface !== undefined &&
        values.regularized_surface !== null
      ) {
        statusData.regularized_surface = values.regularized_surface;
      }

      updateStatus(
        {
          resource: "v1/tickets",
          id: incidence.id,
          values: statusData,
          successNotification: () => ({
            message: t(
              "incidence.closed_success",
              "Incidencia cerrada exitosamente"
            ),
            description: t(
              "incidence.closed_description",
              "La incidencia ha sido cerrada correctamente"
            ),
            type: "success",
          }),
          errorNotification: () => ({
            message: t(
              "incidence.close_error",
              "Error al cerrar la incidencia"
            ),
            description: t(
              "incidence.close_error_description",
              "No se pudo cerrar la incidencia. Por favor, intente nuevamente."
            ),
            type: "error",
          }),
        },
        {
          onSuccess: () => {
            form.resetFields();
            onCancel(); // Close modal and trigger refetch in parent
          },
          onError: (error: any) => {
            // Error handled by errorNotification
          },
        }
      );
    } catch (error) {
      // Form validation failed
    }
  };

  // Handle modal cancel
  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t("incidence.close_incidence", "Cerrar Incidencia")}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText={t("incidence.close", "Cerrar Incidencia")}
      cancelText={t("common.cancel", "Cancelar")}
      width={600}
    >
      <Form form={form} layout="vertical">
        {/* Closure Date */}
        <Form.Item
          label={t("incidence.closure_date", "Fecha de Cierre")}
          name="resolved_date"
          rules={[
            {
              required: true,
              message: t(
                "incidence.closure_date_required",
                "Por favor seleccione la fecha de cierre"
              ),
            },
          ]}
          extra={t(
            "incidence.closure_date_help",
            "Esta fecha se usará como referencia para ordenar incidencias cerradas por año"
          )}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={t(
              "incidence.select_date",
              "Seleccione una fecha"
            )}
          />
        </Form.Item>

        {/* Resolution Notes */}
        <Form.Item
          label={t("incidence.resolution_notes", "Comentario Final")}
          name="resolution_notes"
          rules={[
            {
              required: true,
              message: t(
                "incidence.resolution_notes_required",
                "Por favor ingrese un comentario final"
              ),
            },
            {
              whitespace: true,
              message: t(
                "incidence.resolution_notes_empty",
                "El comentario final no puede estar vacío"
              ),
            },
            {
              min: 10,
              message: t(
                "incidence.resolution_notes_too_short",
                "El comentario final debe tener al menos 10 caracteres"
              ),
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder={t(
              "incidence.resolution_notes_placeholder",
              "Explique cómo se resolvió la incidencia"
            )}
            maxLength={2000}
            showCount
          />
        </Form.Item>

        {/* Regularized Surface (only for "Superficie irregular") */}
        {isSurfaceIrregular && (
          <Form.Item
            label={t(
              "incidence.regularized_surface",
              "Superficie Regularizada (ha)"
            )}
            name="regularized_surface"
            rules={[
              {
                type: "number",
                min: 0,
                message: t(
                  "incidence.regularized_surface_positive",
                  "La superficie debe ser un número positivo"
                ),
              },
            ]}
            extra={t(
              "incidence.regularized_surface_help",
              "Opcional: Ingrese la superficie regularizada en hectáreas"
            )}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder={t(
                "incidence.regularized_surface_placeholder",
                "Ingrese la superficie regularizada"
              )}
              step={0.01}
              min={0}
              precision={2}
            />
          </Form.Item>
        )}

        {/* Information message */}
        <div
          style={{
            padding: "12px",
            backgroundColor: "#e6f7ff",
            border: "1px solid #91d5ff",
            borderRadius: "4px",
            marginTop: "16px",
          }}
        >
          <p style={{ margin: 0, color: "#0050b3" }}>
            {t(
              "incidence.close_info",
              "Al cerrar la incidencia, se registrará como resuelta y no podrá volver a abrirse."
            )}
          </p>
        </div>
      </Form>
    </Modal>
  );
};
